import React, { useEffect, useMemo, useRef, useState } from 'react'
import { MagnifyingGlassIcon, StarIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { pobierzModele, pobierzProducentow, szukajWKatalogu } from '../../api/katalog3d'
import {
  filtrujPozycje, formatujMoc, opcjeMocy, opcjeSerii, opisWyboru, pozycjaListy, sortujPozycje,
} from '../../lib/projekt3d/katalog'
import { ProductImage } from '../ProductImage'
import Lista from '../ui/Lista'
import ListaSzukana from '../ui/ListaSzukana'
import usePreferencjeDoboru from '../../hooks/usePreferencjeDoboru'
import { preferencjeKategorii, producenciZPreferencjami, serieZPreferencjami } from '../../lib/projekt3d/preferencje3d'

/**
 * Wybór modelu z katalogu VoltValue: pole z lupą (wyszukiwanie po nazwie) i pod nim listy Producent,
 * Seria, Moc z modelami producenta od najmniejszej mocy. Wspólne dla projektu dachu 3D
 * (`components/projekt3d/WyborModelu.jsx`) i strony zgodności (`components/compatibility/WyborModeluZgodnosci.jsx`).
 *
 * Kategorie: 'moduly' | 'falownik' | 'magazyn'. Serie i moce liczymy z pobranej listy modeli
 * producenta, więc zmiana filtra nie pyta serwera.
 *
 * Props poza kategorią są opcjonalne, domyślne dają zachowanie projektu 3D:
 * - `onWybierz(obiekt, uwaga)`: wybrana pozycja po `przygotujWybor` (domyślnie sama pozycja listy,
 *   `pozycjaListy` z lib/projekt3d/katalog.js, surowy wiersz API w `pozycja.dane`).
 * - `przygotujWybor(pozycja)`: async, zwraca [obiekt, uwaga]. Liczy się ostatnie kliknięcie.
 * - `pobierzProducentowFn()`: własna lista producentów (np. tylko marki znane modułowi zgodności).
 * - `filtr`: { typ } zawęża listy i wyszukiwarkę po stronie serwera (np. typ 'Hybrydowy' dla falowników).
 * - `dopuscPozycje(pozycja)`: filtr wyników wyszukiwarki po stronie przeglądarki.
 * - `opisPozycji(pozycja)`: druga linijka wiersza (domyślnie `pozycja.opis`).
 * - `onReczny`: wpisywanie parametrów ręcznie; bez niego nie ma tej drogi.
 * - `preferencje`: modele i marki z preferencji doboru konta na górze (domyślnie tak).
 * - `teksty`: podmiana tekstów (strona zgodności podaje tłumaczenia z i18n).
 * - `obecny`: obecny wybór przy zmianie modelu; `obecny.producent` ustawia listę producenta, a nagłówek
 *   „Teraz:” pokazuje `obecny.etykieta` albo `opisWyboru(obecny)` (kształt projektu 3D).
 * - `listaPoFiltrze`: przy dłuższej liście (ponad `KROTKA_LISTA` modeli) modele producenta dopiero po wyborze serii
 *   albo mocy (projekt 3D, prośba właściciela 2026-10-24: pełna lista rozciągała panel boczny); krótka lista i producent
 *   bez serii i mocy do wyboru pokazują listę od razu. Widoczne są wtedy dwa pierwsze modele, reszta pod „Pokaż więcej”
 *   (A2, 2026-10-24), a producenta wybiera się polem z wyszukiwaniem (`ListaSzukana`, A1).
 */

const MIN_ZNAKOW = 2
const KROTKA_LISTA = 6
const OPOZNIENIE_MS = 300

const USTAWIENIA = {
  moduly: { szukaj: 'Szukaj modułu, np. Tiger Neo 450', dowolnaMoc: 'Dowolna moc', moc: 'Moc', obraz: 'panel' },
  falownik: { szukaj: 'Szukaj falownika, np. SUN-10K', dowolnaMoc: 'Dowolna moc', moc: 'Moc', obraz: 'inwerter' },
  magazyn: { szukaj: 'Szukaj magazynu, np. LUNA2000', dowolnaMoc: 'Dowolna pojemność', moc: 'Pojemność', obraz: 'magazyn' },
}

/** Teksty domyślne (projekt 3D, po polsku). `{{n}}` i `{{wszystkich}}` podstawiamy na miejscu. */
export const TEKSTY_WYSZUKIWARKI = {
  brakMocy: 'brak',
  wczytuje: 'Wczytuję…',
  wczytujeModele: 'Wczytuję modele…',
  bladModeli: 'Nie udało się wczytać modeli.',
  sprobujPonownie: 'Spróbuj ponownie',
  brakDlaFiltrow: 'Brak modeli dla tych filtrów.',
  brakProducenta: 'Brak modeli tego producenta w katalogu.',
  modeleZKatalogu: 'Modele z katalogu',
  pokazujemy: 'Pokazujemy {{n}} najczęściej oferowanych z {{wszystkich}}. Resztę znajdziesz wyszukiwarką.',
  teraz: 'Teraz:',
  anuluj: 'Anuluj',
  katalogNiedostepnyReczny: 'Katalog jest teraz niedostępny. Możesz wpisać parametry ręcznie.',
  katalogNiedostepny: 'Katalog jest teraz niedostępny.',
  wpiszRecznie: 'Wpisz ręcznie',
  wyczyscSzukanie: 'Wyczyść wyszukiwanie',
  szukam: 'Szukam…',
  bladSzukania: 'Wyszukiwarka nie odpowiada. Wybierz model z listy poniżej.',
  nieZnaleziono: 'Nie znaleźliśmy takiego modelu.',
  wynikiSzukania: 'Wyniki wyszukiwania',
  alboZListy: 'albo wybierz z listy',
  producent: 'Producent',
  wczytujeProducentow: 'Wczytuję producentów…',
  wybierzProducenta: 'Wybierz producenta',
  wybierzFiltr: 'Wybierz serię albo moc, żeby zobaczyć modele.',
  pokazWiecej: 'Pokaż więcej ({{n}})',
  pokazMniej: 'Pokaż mniej',
  wpiszProducenta: 'Wpisz albo wybierz producenta',
  seria: 'Seria',
  dowolnaSeria: 'Dowolna seria',
  bezSerii: 'Bez serii',
  recznieLink: 'Nie ma modelu w katalogu? Wpisz parametry ręcznie',
  zPreferencji: 'Z Twoich preferencji doboru',
  modeleZPreferencji: 'Modele z preferencji doboru',
}

const podstaw = (tekst, wartosci) => Object.entries(wartosci)
  .reduce((t, [k, v]) => t.split(`{{${k}}}`).join(String(v)), tekst)

const BEZ_PREFERENCJI = { modele: [], producenci: [], serie: [] }

/* ═══ Wiersze i listy ════════════════════════════════════════════════════════════════════════ */

const WierszModelu = ({ kategoria, pozycja: p, laduje, zProducentem = false, onWybierz, opis, teksty }) => (
  <button type="button" onClick={() => onWybierz(p)} disabled={laduje} title={p.nazwa || undefined}
          className="flex w-full items-center gap-2.5 px-2.5 py-1.5 text-left transition hover:bg-blue-50/60 focus:bg-blue-50/60 focus:outline-none disabled:cursor-wait">
    <span className="w-[4.5rem] flex-shrink-0 text-sm font-semibold tabular-nums text-gray-900">
      {p.moc != null ? formatujMoc(kategoria, p.moc) : <span className="text-xs font-normal text-gray-400">{teksty.brakMocy}</span>}
    </span>
    <span className="min-w-0 flex-1">
      <span className="block truncate text-xs text-gray-800">{p.krotka || p.nazwa}</span>
      <span className="block truncate text-[11px] text-gray-500">{[zProducentem && p.producent, opis(p)].filter(Boolean).join(' · ')}</span>
    </span>
    {laduje && <span className="flex-shrink-0 text-[11px] font-semibold text-blue-700">{teksty.wczytuje}</span>}
  </button>
)

const ZWINIETA_LISTA = 2

const ListaModeli = ({ kategoria, modele, widoczne, ladujeId, onWybierz, onPonow, opis, teksty, zwijaj = false }) => {
  const [rozwinieta, setRozwinieta] = useState(false)
  if (modele.stan === 'laduje') return <p role="status" className="text-xs text-gray-500">{teksty.wczytujeModele}</p>
  if (modele.stan === 'blad') {
    return (
      <p className="text-xs text-red-700">
        {teksty.bladModeli}{' '}
        <button type="button" onClick={onPonow} className="font-semibold underline hover:no-underline">{teksty.sprobujPonownie}</button>
      </p>
    )
  }
  if (modele.stan !== 'ok') return null
  if (!widoczne.length) {
    return <p className="text-xs text-gray-500">{modele.lista.length ? teksty.brakDlaFiltrow : teksty.brakProducenta}</p>
  }
  const zwinieta = zwijaj && !rozwinieta && widoczne.length > ZWINIETA_LISTA + 1
  const pokazane = zwinieta ? widoczne.slice(0, ZWINIETA_LISTA) : widoczne
  return (
    <div>
      <ul aria-label={teksty.modeleZKatalogu}
          className="max-h-64 divide-y divide-gray-100 overflow-y-auto overscroll-contain rounded-lg border border-gray-200 bg-white [scrollbar-width:thin]">
        {pokazane.map((p) => (
          <li key={p.id}>
            <WierszModelu kategoria={kategoria} pozycja={p} laduje={ladujeId === p.id} onWybierz={onWybierz} opis={opis} teksty={teksty} />
          </li>
        ))}
      </ul>
      {zwijaj && widoczne.length > ZWINIETA_LISTA + 1 && (
        <button type="button" onClick={() => setRozwinieta((x) => !x)} aria-expanded={!zwinieta}
                className="mt-1 text-xs font-semibold text-blue-700 hover:underline">
          {zwinieta ? podstaw(teksty.pokazWiecej, { n: widoczne.length - ZWINIETA_LISTA }) : teksty.pokazMniej}
        </button>
      )}
      {modele.wszystkich > modele.lista.length && (!zwijaj || !zwinieta) && (
        <p className="mt-1 text-[11px] text-gray-500">
          {podstaw(teksty.pokazujemy, { n: modele.lista.length, wszystkich: modele.wszystkich })}
        </p>
      )}
    </div>
  )
}

/** Modele wskazane w preferencjach doboru konta: na górze wyboru, jedno kliknięcie (2026-10-12). */
const ModelePreferowane = ({ kategoria, modele, ladujeId, onWybierz, teksty }) => (
  <div>
    <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-gray-700">
      <StarIcon className="h-3.5 w-3.5 text-blue-600" aria-hidden="true" /> {teksty.zPreferencji}
    </p>
    <ul aria-label={teksty.modeleZPreferencji} className="divide-y divide-gray-100 overflow-hidden rounded-lg border border-blue-200 bg-blue-50/30">
      {modele.map((m) => {
        const poz = pozycjaListy(kategoria, { id: m.id, name: m.nazwa, manufacturer: m.producent, img_url: m.img })
        return (
          <li key={m.id}>
            <button type="button" onClick={() => onWybierz(poz)} disabled={ladujeId === m.id} title={m.nazwa || undefined}
                    className="flex w-full items-center gap-2.5 px-2.5 py-1.5 text-left transition hover:bg-blue-50 focus:bg-blue-50 focus:outline-none disabled:cursor-wait">
              <ProductImage src={m.img || null} alt="" productType={USTAWIENIA[kategoria].obraz}
                            className="h-8 w-8 flex-shrink-0 rounded-md border border-gray-100 bg-white object-contain" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-xs font-semibold text-gray-900">{poz.krotka || poz.nazwa}</span>
                <span className="block truncate text-[11px] text-gray-500">{m.producent}</span>
              </span>
              {ladujeId === m.id && <span className="flex-shrink-0 text-[11px] font-semibold text-blue-700">{teksty.wczytuje}</span>}
            </button>
          </li>
        )
      })}
    </ul>
  </div>
)

/* ═══ Komponent ══════════════════════════════════════════════════════════════════════════════ */

const domyslnyWybor = (poz) => [poz, null]
const domyslnyOpis = (p) => p.opis

const WyszukiwarkaModelu = ({
  kategoria, obecny = null, onWybierz, onAnuluj = null, onReczny = null,
  przygotujWybor = domyslnyWybor, pobierzProducentowFn = null, filtr = null,
  dopuscPozycje = null, opisPozycji = domyslnyOpis, preferencje = true,
  placeholder = null, teksty: tekstyWlasne = null, listaPoFiltrze = false,
}) => {
  const u = USTAWIENIA[kategoria]
  const teksty = useMemo(() => ({ ...TEKSTY_WYSZUKIWARKI, ...(tekstyWlasne || {}) }), [tekstyWlasne])
  const etykietaSzukania = placeholder || u.szukaj
  const typ = filtr?.typ || null
  // Preferencje doboru konta (2026-10-12): wybrane modele na górze, producenci i serie z preferencji pierwsi.
  const prefs = usePreferencjeDoboru()
  const pref = useMemo(() => (preferencje ? preferencjeKategorii(prefs, kategoria) : BEZ_PREFERENCJI),
    [prefs, kategoria, preferencje])
  const [proba, setProba] = useState(0)
  const [producenci, setProducenci] = useState({ stan: 'laduje', lista: [] })
  const [producent, setProducent] = useState('')
  const [seria, setSeria] = useState('')
  const [moc, setMoc] = useState('')
  const [modele, setModele] = useState({ stan: 'brak', lista: [], wszystkich: 0 })
  const [fraza, setFraza] = useState('')
  const [wyniki, setWyniki] = useState({ stan: 'brak', lista: [] })
  const [ladujeId, setLadujeId] = useState(null)
  const ostatniWybor = useRef(0)
  const poleSzukania = useRef(null)
  const listaWynikow = useRef(null)
  const obecnyProducent = obecny?.producent || ''
  // Funkcja z propsów zmienia się przy każdym renderze rodzica: trzymamy ją w ref, żeby nie pytać serwera w kółko.
  const zrodloProducentow = useRef(pobierzProducentowFn)
  zrodloProducentow.current = pobierzProducentowFn

  // Producenci. Przy zmianie modelu od razu lista modeli obecnego producenta.
  useEffect(() => {
    let aktualne = true
    setProducenci({ stan: 'laduje', lista: [] })
    const pobierz = zrodloProducentow.current || (() => pobierzProducentow(kategoria))
    Promise.resolve().then(pobierz)
      .then((lista) => {
        if (!aktualne) return
        setProducenci({ stan: 'ok', lista })
        setProducent((p) => p || (lista.includes(obecnyProducent) ? obecnyProducent : ''))
      })
      .catch(() => { if (aktualne) setProducenci({ stan: 'blad', lista: [] }) })
    return () => { aktualne = false }
  }, [kategoria, proba, obecnyProducent])

  // Bez obecnego wyboru od razu lista modeli pierwszego producenta z preferencji (jeśli jest w katalogu).
  useEffect(() => {
    if (producenci.stan !== 'ok') return
    const pierwszy = pref.producenci.find((x) => producenci.lista.includes(x))
    if (pierwszy) setProducent((p) => p || pierwszy)
  }, [producenci, pref.producenci])

  // Wszystkie modele producenta jednym zapytaniem; serie i moce liczymy z nich na miejscu.
  useEffect(() => {
    if (!producent) {
      setModele({ stan: 'brak', lista: [], wszystkich: 0 })
      return undefined
    }
    let aktualne = true
    setModele({ stan: 'laduje', lista: [], wszystkich: 0 })
    pobierzModele(kategoria, { producent, typ })
      .then(({ items, count }) => {
        if (aktualne) setModele({ stan: 'ok', lista: items.map((x) => pozycjaListy(kategoria, x)), wszystkich: count })
      })
      .catch(() => { if (aktualne) setModele({ stan: 'blad', lista: [], wszystkich: 0 }) })
    return () => { aktualne = false }
  }, [kategoria, producent, proba, typ])

  const q = fraza.trim()
  const szukamy = q.length >= MIN_ZNAKOW
  useEffect(() => {
    if (!szukamy) {
      setWyniki({ stan: 'brak', lista: [] })
      return undefined
    }
    let aktualne = true
    // Poprzednie wyniki zostają (przygaszone), dopóki nie przyjdą nowe.
    setWyniki((w) => ({ ...w, stan: 'szuka' }))
    const t = setTimeout(() => {
      szukajWKatalogu(kategoria, q, { typ })
        .then((lista) => { if (aktualne) setWyniki({ stan: 'ok', lista: lista.map((x) => pozycjaListy(kategoria, x)) }) })
        .catch(() => { if (aktualne) setWyniki({ stan: 'blad', lista: [] }) })
    }, OPOZNIENIE_MS)
    return () => {
      aktualne = false
      clearTimeout(t)
    }
  }, [kategoria, q, szukamy, typ])

  const wynikiWidoczne = useMemo(
    () => (dopuscPozycje ? wyniki.lista.filter(dopuscPozycje) : wyniki.lista),
    [wyniki.lista, dopuscPozycje],
  )

  const serie = useMemo(() => opcjeSerii(modele.lista), [modele.lista])
  const seriaAktywna = serie.some((s) => s.value === seria) ? seria : ''
  const wSerii = useMemo(() => filtrujPozycje(modele.lista, { seria: seriaAktywna }), [modele.lista, seriaAktywna])
  const moce = useMemo(() => opcjeMocy(wSerii), [wSerii])
  const mocAktywna = moce.some((m) => String(m) === moc) ? moc : ''
  const widoczne = useMemo(() => sortujPozycje(filtrujPozycje(wSerii, { moc: mocAktywna })), [wSerii, mocAktywna])

  /** Wybrany wiersz: `przygotujWybor` może dociągnąć pełne dane modelu. Liczy się ostatnie kliknięcie. */
  const wybierzPozycje = async (poz) => {
    const moje = ++ostatniWybor.current
    setLadujeId(poz.id)
    const [obiekt, uwaga] = await przygotujWybor(poz)
    if (moje !== ostatniWybor.current) return
    setLadujeId(null)
    onWybierz(obiekt, uwaga)
  }

  const klawiszePola = (e) => {
    if (e.key === 'ArrowDown') {
      const pierwszy = listaWynikow.current?.querySelector('button')
      if (pierwszy) {
        e.preventDefault()
        pierwszy.focus()
      }
    } else if (e.key === 'Enter' && wyniki.stan === 'ok' && wynikiWidoczne.length) {
      e.preventDefault()
      wybierzPozycje(wynikiWidoczne[0])
    } else if (e.key === 'Escape' && fraza) {
      e.preventDefault()
      setFraza('')
    }
  }
  const klawiszeWynikow = (e) => {
    const przyciski = [...(listaWynikow.current?.querySelectorAll('button') || [])]
    const i = przyciski.indexOf(document.activeElement)
    if (e.key === 'ArrowDown' && i >= 0 && i < przyciski.length - 1) {
      e.preventDefault()
      przyciski[i + 1].focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (i > 0) przyciski[i - 1].focus()
      else poleSzukania.current?.focus()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      poleSzukania.current?.focus()
    }
  }

  const naglowek = obecny && onAnuluj && (
    <div className="flex items-center justify-between gap-2 text-xs">
      <span className="min-w-0 truncate text-gray-500">{teksty.teraz} <span className="font-semibold text-gray-700">{obecny.etykieta || opisWyboru(obecny)}</span></span>
      <button type="button" onClick={onAnuluj} className="flex-shrink-0 font-semibold text-blue-700 hover:underline">{teksty.anuluj}</button>
    </div>
  )

  if (producenci.stan === 'blad') {
    return (
      <div className="space-y-2.5">
        {naglowek}
        <p className="text-sm text-gray-700">{onReczny ? teksty.katalogNiedostepnyReczny : teksty.katalogNiedostepny}</p>
        <div className="flex flex-wrap gap-2">
          {onReczny && (
            <button type="button" onClick={onReczny} className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700">
              {teksty.wpiszRecznie}
            </button>
          )}
          <button type="button" onClick={() => setProba((x) => x + 1)}
                  className="rounded-lg border-2 border-blue-200 px-3 py-1.5 text-sm font-semibold text-blue-700 hover:border-blue-400">
            {teksty.sprobujPonownie}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2.5">
      {naglowek}
      {pref.modele.length > 0 && !szukamy && (
        <ModelePreferowane kategoria={kategoria} modele={pref.modele} ladujeId={ladujeId} onWybierz={wybierzPozycje} teksty={teksty} />
      )}
      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input ref={poleSzukania} type="text" value={fraza} onChange={(e) => setFraza(e.target.value)} onKeyDown={klawiszePola}
               placeholder={etykietaSzukania} aria-label={etykietaSzukania} autoComplete="off" spellCheck={false}
               className="w-full rounded-lg border-2 border-blue-200 bg-blue-50/40 py-1.5 pl-8 pr-8 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:bg-white focus:outline-none" />
        {fraza && (
          <button type="button" onClick={() => { setFraza(''); poleSzukania.current?.focus() }} aria-label={teksty.wyczyscSzukanie}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-700">
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {szukamy && (
        <div aria-live="polite">
          {wyniki.stan === 'szuka' && !wynikiWidoczne.length && <p className="text-xs text-gray-500">{teksty.szukam}</p>}
          {wyniki.stan === 'blad' && <p className="text-xs text-red-700">{teksty.bladSzukania}</p>}
          {wyniki.stan === 'ok' && !wynikiWidoczne.length && <p className="text-xs text-gray-500">{teksty.nieZnaleziono}</p>}
          {wynikiWidoczne.length > 0 && (
            <ul ref={listaWynikow} onKeyDown={klawiszeWynikow} aria-label={teksty.wynikiSzukania}
                className={`divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-opacity ${wyniki.stan === 'szuka' ? 'opacity-60' : ''}`}>
              {wynikiWidoczne.map((p) => (
                <li key={p.id}>
                  <WierszModelu kategoria={kategoria} pozycja={p} zProducentem laduje={ladujeId === p.id} onWybierz={wybierzPozycje}
                                opis={opisPozycji} teksty={teksty} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 text-xs text-gray-400" aria-hidden="true">
        <span className="h-px flex-1 bg-gray-200" />
        {teksty.alboZListy}
        <span className="h-px flex-1 bg-gray-200" />
      </div>

      {listaPoFiltrze ? (
        <ListaSzukana wartosc={producent} disabled={producenci.stan !== 'ok'} etykieta={teksty.producent}
                      pusty={producenci.stan === 'laduje' ? teksty.wczytujeProducentow : teksty.wpiszProducenta}
                      opcje={producenciZPreferencjami(producenci.lista, pref.producenci)}
                      onZmiana={(v) => { setProducent(v); setSeria(''); setMoc('') }} />
      ) : (
        <Lista wartosc={producent} disabled={producenci.stan !== 'ok'} etykieta={teksty.producent}
               pusty={producenci.stan === 'laduje' ? teksty.wczytujeProducentow : teksty.wybierzProducenta}
               opcje={producenciZPreferencjami(producenci.lista, pref.producenci)}
               onZmiana={(v) => { setProducent(v); setSeria(''); setMoc('') }} />
      )}

      {producent && modele.stan === 'ok' && (serie.length > 0 || moce.length > 1) && (
        <div className="grid grid-cols-2 gap-2">
          <Lista wartosc={seriaAktywna} disabled={!serie.length} etykieta={teksty.seria} maly
                 pustaOpcja={serie.length ? teksty.dowolnaSeria : teksty.bezSerii}
                 opcje={serieZPreferencjami(serie, pref.serie, producent)}
                 onZmiana={(v) => { setSeria(v); setMoc('') }} />
          <Lista wartosc={mocAktywna} disabled={moce.length < 2} etykieta={teksty.moc || u.moc} maly
                 pustaOpcja={teksty.dowolnaMoc || u.dowolnaMoc}
                 opcje={moce.map((m) => ({ value: String(m), label: formatujMoc(kategoria, m) }))}
                 onZmiana={(v) => setMoc(v)} />
        </div>
      )}

      {producent && (listaPoFiltrze && modele.stan === 'ok' && modele.lista.length > KROTKA_LISTA && (serie.length > 0 || moce.length > 1)
        && !seriaAktywna && !mocAktywna
        ? <p className="text-xs text-gray-500">{teksty.wybierzFiltr}</p>
        : (
          <ListaModeli key={`${producent}|${seriaAktywna}|${mocAktywna}`} kategoria={kategoria} modele={modele} widoczne={widoczne} ladujeId={ladujeId}
                       onWybierz={wybierzPozycje} onPonow={() => setProba((x) => x + 1)} opis={opisPozycji} teksty={teksty} zwijaj={listaPoFiltrze} />
        ))}

      {onReczny && (
        <button type="button" onClick={onReczny} className="text-xs font-semibold text-gray-600 hover:text-blue-700">
          {teksty.recznieLink}
        </button>
      )}
    </div>
  )
}

export default WyszukiwarkaModelu
